import React, { useCallback } from 'react';

import PromptBox from '../../PromptBox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import StateSelect from '../components/StateSelect';
import { ACCIDENT_TYPES } from '../constants';
import { IntakeFormData } from '../form/types';
import { CompanionRowsSection } from './CompanionRowsSection';

interface IncidentInfoSectionProps {
  form: IntakeFormData;
  intakeId: string;
  canEditCompanions?: boolean;
  onFieldChange: (
    section: keyof IntakeFormData,
    field: string,
    value: string | boolean | string[] | number | null | IntakeFormData['companions'],
  ) => void;
  onCompanionsChange?: (companions: IntakeFormData['companions']) => void;
  companionRoot?: string | null;
  isCompanionPrimary?: boolean;
}

const IncidentInfoSection: React.FC<IncidentInfoSectionProps> = ({
  form,
  intakeId,
  canEditCompanions = true,
  onFieldChange,
  onCompanionsChange,
  companionRoot,
  isCompanionPrimary = true,
}) => {
  const handleInputChange = useCallback(
    (field: string, value: string | boolean | string[] | number | null) => {
      onFieldChange('incidentInfo', field, value);
    },
    [onFieldChange],
  );

  const handleCompanionsChange = useCallback(
    (companions: IntakeFormData['companions']) => {
      if (onCompanionsChange) {
        onCompanionsChange(companions);
        return;
      }
      onFieldChange(
        'companions' as keyof IntakeFormData,
        'companions',
        companions,
      );
    },
    [onFieldChange, onCompanionsChange],
  );

  return (
    <div className="space-y-3">
      <PromptBox
        title="Suggested Questions"
        suggestions={[
          'What exactly happened during the incident?',
          'Where did the incident occur?',
          'What were the weather conditions?',
          'Were there any witnesses present?',
          'Did you call the police or emergency services?',
          'Do you have any photos or evidence from the scene?',
        ]}
      />

      {/* Accident Details & Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <CardTitle className="text-base">
              Accident Details & Timeline
            </CardTitle>
          </div>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Basic information about when and where the incident occurred
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="incidentDate" className="text-sm font-medium">
                Date
              </Label>
              <Input
                id="incidentDate"
                type="date"
                value={form.incidentInfo.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                aria-label="Incident date"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="incidentTime" className="text-sm font-medium">
                Time
              </Label>
              <Input
                id="incidentTime"
                type="time"
                value={form.incidentInfo.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                aria-label="Incident time"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="location" className="text-sm font-medium">
                Location
              </Label>
              <div className="flex gap-2">
                <Input
                  id="location"
                  value={form.incidentInfo.location}
                  onChange={(e) =>
                    handleInputChange('location', e.target.value)
                  }
                  placeholder="Street address or intersection"
                  aria-label="Incident location"
                  tabIndex={0}
                  className="text-sm flex-1"
                />
                <StateSelect
                  value={form.incidentInfo.state}
                  onChange={(value) => handleInputChange('state', value)}
                  placeholder="State"
                  ariaLabel="Incident state"
                  className="text-sm w-1/3"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="weather" className="text-sm font-medium">
                Weather Conditions
              </Label>
              <Input
                id="weather"
                value={form.incidentInfo.weather}
                onChange={(e) => handleInputChange('weather', e.target.value)}
                placeholder="e.g., Clear, Rainy, Snowy"
                aria-label="Weather conditions"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="fromTo" className="text-sm font-medium">
                From/To
              </Label>
              <Input
                id="fromTo"
                value={form.incidentInfo.fromTo}
                onChange={(e) => handleInputChange('fromTo', e.target.value)}
                placeholder="e.g., Home to work"
                aria-label="From/To destination"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="accidentType" className="text-sm font-medium">
                Accident Type
              </Label>
              <Select
                value={form.incidentInfo.type}
                onValueChange={(value) => handleInputChange('type', value)}
              >
                <SelectTrigger
                  id="accidentType"
                  tabIndex={0}
                  className="text-sm"
                >
                  <SelectValue placeholder="Select accident type" />
                </SelectTrigger>
                <SelectContent>
                  {ACCIDENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="text-sm">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 space-y-1">
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              value={form.incidentInfo.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Provide a detailed description of what happened..."
              rows={4}
              aria-label="Incident description"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Present Complaints & Injuries */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Present Complaints & Injuries
          </CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Current symptoms and injuries from the incident
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-1">
            <Label htmlFor="presentComplaints" className="text-sm font-medium">
              Present Complaints
            </Label>
            <Textarea
              id="presentComplaints"
              value={form.incidentInfo.presentComplaints}
              onChange={(e) =>
                handleInputChange('presentComplaints', e.target.value)
              }
              placeholder="Describe your current symptoms, pain levels, and any ongoing issues..."
              rows={4}
              aria-label="Present complaints"
              tabIndex={0}
              className="text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Emergency Medical Care */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Emergency Medical Care</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Information about immediate medical response
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="ems"
                checked={form.incidentInfo.ems}
                onCheckedChange={(checked) => handleInputChange('ems', checked)}
                tabIndex={0}
                className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
              />
              <Label
                htmlFor="ems"
                className="cursor-pointer text-sm font-medium text-gray-700"
              >
                EMS Called
              </Label>
            </div>

            <div className="space-y-1">
              <Label htmlFor="hospital" className="text-sm font-medium">
                Hospital/Medical Facility
              </Label>
              <Input
                id="hospital"
                value={form.incidentInfo.hospital}
                onChange={(e) => handleInputChange('hospital', e.target.value)}
                placeholder="Hospital or clinic name"
                aria-label="Hospital or medical facility"
                tabIndex={0}
                className="text-sm"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="currentlyTreating"
                checked={form.incidentInfo.currentlyTreating}
                onCheckedChange={(checked) =>
                  handleInputChange('currentlyTreating', checked)
                }
                tabIndex={0}
                className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
              />
              <Label
                htmlFor="currentlyTreating"
                className="cursor-pointer text-sm font-medium text-gray-700"
              >
                Currently Under Treatment
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supporting Evidence */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Supporting Evidence</CardTitle>
          <CardDescription className="text-xs text-gray-500 leading-tight">
            Documentation and evidence related to the incident
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            {/* Row 1: Police Report Filed checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="policeReport"
                checked={form.incidentInfo.policeReport}
                onCheckedChange={(checked) =>
                  handleInputChange('policeReport', checked)
                }
                tabIndex={0}
                className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
              />
              <Label
                htmlFor="policeReport"
                className="cursor-pointer text-sm font-medium text-gray-700"
              >
                Police Report Filed
              </Label>
            </div>
            {/* Row 2: Police Department and Report Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="policeDept" className="text-sm font-medium">
                  Police Department
                </Label>
                <Input
                  id="policeDept"
                  value={form.incidentInfo.policeDept}
                  onChange={(e) =>
                    handleInputChange('policeDept', e.target.value)
                  }
                  placeholder="Department name"
                  aria-label="Police department"
                  tabIndex={0}
                  className="text-sm"
                  disabled={!form.incidentInfo.policeReport}
                />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="policeReportNumber"
                  className="text-sm font-medium"
                >
                  Police Report Number
                </Label>
                <Input
                  id="policeReportNumber"
                  value={form.incidentInfo.policeReportNumber}
                  onChange={(e) =>
                    handleInputChange('policeReportNumber', e.target.value)
                  }
                  placeholder="Report number"
                  aria-label="Police report number"
                  tabIndex={0}
                  className="text-sm"
                  disabled={!form.incidentInfo.policeReport}
                />
              </div>
            </div>
            {/* Row 3: Photos Available checkbox */}
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="photos"
                checked={form.incidentInfo.photos}
                onCheckedChange={(checked) =>
                  handleInputChange('photos', checked)
                }
                tabIndex={0}
                className="border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white hover:border-gray-400 focus-visible:ring-blue-500/20"
              />
              <Label
                htmlFor="photos"
                className="cursor-pointer text-sm font-medium text-gray-700"
              >
                Photos Available
              </Label>
            </div>
            {/* Row 4: Witnesses input */}
            <div className="space-y-1 mt-2">
              <Label htmlFor="witnesses" className="text-sm font-medium">
                Witnesses
              </Label>
              <Input
                id="witnesses"
                value={form.incidentInfo.witnesses}
                onChange={(e) => handleInputChange('witnesses', e.target.value)}
                placeholder="Names and contact information"
                aria-label="Witnesses"
                tabIndex={0}
                className="text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Companion Cases Section */}
      <CompanionRowsSection
        intakeId={intakeId}
        companions={form.companions || []}
        onCompanionsChange={handleCompanionsChange}
        canEdit={canEditCompanions}
        isPrimary={isCompanionPrimary}
        primaryIntakeId={companionRoot}
      />
    </div>
  );
};

export default React.memo(IncidentInfoSection);
